import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysInvoiceAiModel } from './queryKeys';

const zodDeleteInvoiceAiModelSettingsEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('invoice'),
  z.literal('ai_model_settings'),
  z.uuid(),
]);

type DeleteInvoiceAiModelSettingsResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Deletes the settings of an invoice AI model.
 *
 * @param invoiceAiModelSettingsId - The ID of the invoice AI model settings to delete.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_ai_model_settings_vadmin_invoice_ai_model_settings__model_id__delete
 */
export const deleteInvoiceAiModelSettings = async (
  invoiceAiModelSettingsId: UUID,
) => {
  try {
    const response = await vestigasApi
      .delete(
        ENDPOINT.INVOICE_AI_MODEL.SETTINGS.DELETE(invoiceAiModelSettingsId),
        {
          validate: {
            pathSegments: zodDeleteInvoiceAiModelSettingsEndpointSchema,
          },
        },
      )
      .json<DeleteInvoiceAiModelSettingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error deleting invoice AI model settings',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for deleting the settings of an invoice AI model using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/delete_invoice_ai_model_settings_vadmin_invoice_ai_model_settings__model_id__delete
 */
export const useMutationDeleteInvoiceAiModelSettings = (
  options?: UseMutationOptions<
    DeleteInvoiceAiModelSettingsResponse,
    unknown,
    UUID
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceAiModelSettingsId: UUID) =>
      deleteInvoiceAiModelSettings(invoiceAiModelSettingsId),
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
